import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import CardRegistrationModal from '../../components/ui/CardRegistrationModal';
import MetricCard from './components/MetricCard';
import QuickActions from './components/QuickActions';
import ActivityFeed from './components/ActivityFeed';
import AlertsPanel from './components/AlertsPanel';
import UsageChart from './components/UsageChart';
import { useGetallFunctionQuery } from 'store/dynamicApi';

const DashboardOverview = () => {
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    metrics: {
      totalActiveCards: 1247,
      paymentsProcessed: 89650,
      restrictedCards: 23,
      totalUsers: 456
    },
    trends: {
      activeCardsChange: '+12%',
      activeCardsChangeType: 'positive',
      paymentsChange: '+8.5%',
      paymentsChangeType: 'positive',
      restrictedCardsChange: '-5%',
      restrictedCardsChangeType: 'positive',
      usersChange: '+15%',
      usersChangeType: 'positive'
    }
  });

  const [recentActivities] = useState([
    {
      type: 'card_registered',
      user: 'John Smith',
      action: 'registered a new card',
      target: 'Business Card #NFC-2024-001',
      timestamp: new Date(Date.now() - 300000),
      cardId: 'NFC-2024-001'
    },
    {
      type: 'payment_processed',
      user: 'Sarah Johnson',
      action: 'processed payment for',
      target: 'Team Account',
      timestamp: new Date(Date.now() - 600000),
      amount: '$299.99'
    },
    {
      type: 'card_restricted',
      user: 'System',
      action: 'restricted card due to security policy',
      target: 'NFC-2024-002',
      timestamp: new Date(Date.now() - 900000),
      cardId: 'NFC-2024-002'
    },
    {
      type: 'user_created',
      user: 'Admin',
      action: 'created new user account for',
      target: 'Michael Chen',
      timestamp: new Date(Date.now() - 1200000)
    },
    {
      type: 'card_activated',
      user: 'Lisa Wong',
      action: 'activated card',
      target: 'Personal Card #NFC-2024-003',
      timestamp: new Date(Date.now() - 1800000),
      cardId: 'NFC-2024-003'
    },
    {
      type: 'payment_processed',
      user: 'David Miller',
      action: 'processed payment for',
      target: 'Individual Account',
      timestamp: new Date(Date.now() - 2400000),
      amount: '$49.99'
    }
  ]);

  const [alerts] = useState([
    {
      type: 'restricted_card',
      title: 'Card Requires Attention',
      description: 'NFC-2024-002 has been restricted due to suspicious activity and needs manual review.',
      priority: 'high',
      timestamp: new Date(Date.now() - 1800000),
      actionable: true
    },
    {
      type: 'billing_issue',
      title: 'Payment Failed',
      description: 'Team account billing for TechCorp failed. Credit card may be expired.',
      priority: 'medium',
      timestamp: new Date(Date.now() - 3600000),
      actionable: true
    },
    {
      type: 'system_alert',
      title: 'System Maintenance',
      description: 'Scheduled maintenance window tonight from 2:00 AM - 4:00 AM EST.',
      priority: 'low',
      timestamp: new Date(Date.now() - 7200000),
      actionable: false
    }
  ]);

  const [chartData] = useState([
    { name: 'Mon', cards: 145, payments: 89 },
    { name: 'Tue', cards: 152, payments: 95 },
    { name: 'Wed', cards: 148, payments: 87 },
    { name: 'Thu', cards: 161, payments: 102 },
    { name: 'Fri', cards: 159, payments: 98 },
    { name: 'Sat', cards: 142, payments: 76 },
    { name: 'Sun', cards: 138, payments: 71 }
  ]);

  const { data: cards } = useGetallFunctionQuery({ url: "/card/all" });
  const { data: users } = useGetallFunctionQuery({ url: "/users" });
  const { data: providers } = useGetallFunctionQuery({ url: "/providers" });
  const { data: Contacts } = useGetallFunctionQuery({ url: "/card/contacts" });

  const handleRegisterCard = () => {
    setShowRegistrationModal(true);
  };

  const handleViewActivity = () => {
    window.location.href = '/reports-analytics';
  };

  const handleCardRegistration = (cardData) => {
    console.log('New card registered:', cardData);
    // In a real app, this would update the dashboard data
    setDashboardData(prev => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        totalActiveCards: prev.metrics.totalActiveCards + 1
      }
    }));
  };

  useEffect(() => {
    // Simulate real-time data updates
    const interval = setInterval(() => {
      // In a real app, this would fetch fresh data from an API
      console.log('Refreshing dashboard data...');
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar />

      {/* Main Content */}
      <main className="lg:ml-60 pt-16">
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-text-primary">Dashboard Overview</h1>
              <p className="text-text-muted mt-1">
                Monitor your NFC card fleet performance and system metrics
              </p>
            </div>
            <div className="text-sm text-text-muted">
              Last updated: {new Date().toLocaleTimeString()}
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Total Cards"
              value={cards?.length || 0}
              // value={dashboardData.metrics.totalActiveCards.toLocaleString()}
              // change={dashboardData.trends.activeCardsChange}
              // changeType={dashboardData.trends.activeCardsChangeType}
              icon="CreditCard"
              trend={true}
            />
            <MetricCard
              title="Total Users"
              value={users?.length || 0}
              // value={`$${dashboardData.metrics.paymentsProcessed.toLocaleString()}`}
              // change={dashboardData.trends.paymentsChange}
              // changeType={dashboardData.trends.paymentsChangeType}
              icon="Users"
              trend={true}
            />
            <MetricCard
              title="Total Providers"
              value={providers?.length || 0}
              // value={`$${dashboardData.metrics.paymentsProcessed.toLocaleString()}`}
              // change={dashboardData.trends.paymentsChange}
              // changeType={dashboardData.trends.paymentsChangeType}
              icon="Users"
              trend={true}
            />
            {/* <MetricCard
              title="Restricted Cards"
              value={dashboardData.metrics.restrictedCards.toString()}
              change={dashboardData.trends.restrictedCardsChange}
              changeType={dashboardData.trends.restrictedCardsChangeType}
              icon="Shield"
              trend={true}
            /> */}
            <MetricCard
              title="Total Contacts"
              value={Contacts?.length || 0}
              // value={dashboardData.metrics.totalUsers.toString()}
              // change={dashboardData.trends.usersChange}
              // changeType={dashboardData.trends.usersChangeType}
              icon="Users"
              trend={true}
            />
          </div>

          {/* Quick Actions */}
          <QuickActions
            onRegisterCard={handleRegisterCard}
            onViewActivity={handleViewActivity}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Activity Feed - Takes 2 columns on xl screens */}
            <div className="xl:col-span-2 space-y-6">
              <ActivityFeed activities={recentActivities} />
              <UsageChart data={chartData} title="Weekly Card Usage & Payments" />
            </div>

            {/* Alerts Panel - Takes 1 column on xl screens */}
            <div className="xl:col-span-1">
              <AlertsPanel alerts={alerts} />
            </div>
          </div>

          {/* Additional Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <UsageChart
              data={[
                { name: 'Individual', cards: 847, payments: 45230 },
                { name: 'Team', cards: 400, payments: 44420 }
              ]}
              title="Cards by User Type"
            />
            <div className="bg-surface border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-4">System Status</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">API Response Time</span>
                  <span className="text-success font-mono">142ms</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Database Connection</span>
                  <span className="text-success">Healthy</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">Payment Gateway</span>
                  <span className="text-success">Online</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">NFC Service</span>
                  <span className="text-success">Active</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary">System Uptime</span>
                  <span className="text-text-primary font-mono">99.9%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      <CardRegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
        onSubmit={handleCardRegistration}
      />
    </div>
  );
};

export default DashboardOverview;